import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit'
import Container from 'typedi'
import i18n from 'i18n'

import { ApplicationState } from '@/state/root.models'
import { RestApiService } from '@/services'
import {
  setLoadingFullAction,
  setSnackbarAction,
} from '@/state/ui/global/slices/index.slices'

import { jsonToQueryString } from '@/utils'
import { BALANCE_REPORT } from './api.routes'
import { getBalanceReportMock, USE_BALANCE_REPORT_MOCK } from './mock'

import {
  BalanceReportResponse,
  BalanceReportState,
  GetBalanceReportPayload,
} from '../models'
import ActionTypes from '../constants'
import { BalanceReportActions } from '../slices/index.slices'

const restApiService: RestApiService = Container.get(RestApiService)

export const getBalanceReportThunk = createAsyncThunk(
  ActionTypes.GET_BALANCE_REPORT,
  async (payload: GetBalanceReportPayload, thunkAPI) => {
    try {
      const { xlsx, ...queries } = payload
      const { auth } = thunkAPI.getState() as ApplicationState

      if (!xlsx) {
        thunkAPI.dispatch(BalanceReportActions.setIsloadingAction(true))
      }

      thunkAPI.dispatch(setLoadingFullAction(true))

      // TEMPORARY: resolve local fixture until the casino-auth endpoint exists.
      if (USE_BALANCE_REPORT_MOCK) {
        const mockResponse = getBalanceReportMock(payload)
        thunkAPI.dispatch(setLoadingFullAction(false))
        return mockResponse
      }

      restApiService.requestParams = {
        method: 'GET',
        endpoint: BALANCE_REPORT.FIND,
        isIncentiveAPI: true,
        queries: jsonToQueryString(queries),
      }

      const response: BalanceReportResponse = await restApiService.getData(
        auth.brand.siteId.toString(),
        auth.token || '',
      )

      thunkAPI.dispatch(setLoadingFullAction(false))

      if (response.status && response.status !== 200) {
        const message =
          typeof response.message === 'string'
            ? response.message
            : i18n.t('messages.errorOccurred')

        thunkAPI.dispatch(
          setSnackbarAction({
            message,
            open: true,
            variant: 'error',
          }),
        )
      }

      return response
    } catch (error) {
      thunkAPI.dispatch(setLoadingFullAction(false))
      throw error
    }
  },
)

const getBalanceReportExtraReducer = (
  builder: ActionReducerMapBuilder<BalanceReportState>,
) => {
  builder.addCase(
    getBalanceReportThunk.fulfilled,
    (state, { payload, meta }) => {
      const { xlsx } = meta?.arg

      state.isLoading = false
      state.reports = xlsx ? state.reports : payload.docs || []
      state.pagination = xlsx
        ? state.pagination
        : {
            ...state.pagination,
            total: payload.total,
            pages: payload.pages,
            page: payload.page,
            limit: payload.limit,
          }
    },
  )
}

export default getBalanceReportExtraReducer
